import type { ReactNode } from 'react'
import { motion } from 'motion/react'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay'
import { AmbientBackdrop } from '@/components/ui/AmbientBackdrop'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { useGameAudio } from '@/contexts/GameAudioProvider'

interface ScreenShellProps {
  title: string
  children: ReactNode
  showBack?: boolean
  showCurrency?: boolean
  actions?: ReactNode
  scroll?: boolean
}

export function ScreenShell({
  title,
  children,
  showBack = true,
  showCurrency = true,
  actions,
  scroll = true,
}: ScreenShellProps) {
  const { back, canGoBack } = useNavigation()
  const { save } = useProgress()
  const audio = useGameAudio()

  return (
    <motion.section
      className="relative flex h-full flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
    >
      <AmbientBackdrop />
      <header className="relative w-full max-w-2xl mx-auto flex items-center gap-3 px-4 pt-4 pb-3">
        {showBack && canGoBack && (
          <button
            aria-label="Back"
            onClick={() => {
              audio.play('back')
              back()
            }}
            className="grid place-items-center w-11 h-11 rounded-full bg-[var(--surface-raised)] shadow-emboss text-[var(--text-dim)] hover:text-[var(--text)] active:scale-95"
          >
            <PixelIcon name="arrow-left" size={20} />
          </button>
        )}
        <h1 className="font-display text-2xl font-extrabold text-[var(--text)] tracking-tight">{title}</h1>
        <div className="ml-auto flex items-center gap-2">
          {actions}
          {showCurrency && <CurrencyDisplay coins={save.economy.coins} gems={save.economy.gems} />}
        </div>
      </header>
      <div className={['relative flex-1 w-full max-w-2xl mx-auto px-4 pb-[calc(1rem+var(--safe-bottom))]', scroll ? 'overflow-y-auto no-scrollbar' : 'overflow-hidden'].join(' ')}>
        {children}
      </div>
    </motion.section>
  )
}
